import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  DashboardLocation,
  DashboardLocationScore,
  DashboardWeek,
} from '../models/dashboard';
import {
  CRITICAL_SCORE,
  scoreColor,
  scoreSeries,
  shortAuditName,
  shortDate,
  weekRange,
  weeksAgoLabel,
} from '../lib/dashboard';
import StoreScoreChart from './StoreScoreChart';

// Weekly dashboard (GRI-957), "All locations" overview, as in the design prototype:
// week selector, portfolio average, one card per location and the store score chart.
// Every number comes from a published snapshot.

type Props = {
  weeks: DashboardWeek[]; // newest first, at least one
  selected: number; // index into weeks
  onSelect: (index: number) => void;
  today?: Date;
  // Link to a location's report; cards are not links without it.
  reportLink?: (locationId: string) => string;
};

const AUDIT_COLORS = ['#73777F', '#325882', '#B75E32', '#00848D', '#8D5ED2'];

export const WeekSelector = ({ weeks, selected, onSelect, today }: Props) => {
  const [open, setOpen] = useState(false);
  const week = weeks[selected];
  const olderOff = selected >= weeks.length - 1;
  const newerOff = selected <= 0;
  return (
    <>
      <WeekBar>
        <IconButton
          aria-label="Previous week"
          disabled={olderOff}
          onClick={() => onSelect(selected + 1)}
        >
          ‹
        </IconButton>
        <WeekButton
          aria-haspopup="dialog"
          aria-label="Choose a week"
          onClick={() => setOpen(true)}
        >
          <span className="range">{weekRange(week)}</span>
          <span className="ago">{weeksAgoLabel(week.weekStart, today)} ▾</span>
        </WeekButton>
        <IconButton
          aria-label="Next week"
          disabled={newerOff}
          onClick={() => onSelect(selected - 1)}
        >
          ›
        </IconButton>
      </WeekBar>
      {open && (
        <Scrim>
          <button
            className="backdrop"
            aria-label="Close"
            onClick={() => setOpen(false)}
          />
          <Sheet role="dialog" aria-modal="true" aria-label="Choose a week">
            <div className="title">Choose a week</div>
            {weeks.map((w, i) => (
              <button
                key={w.weekStart}
                className="item"
                aria-current={i === selected}
                style={{
                  background: i === selected ? '#D7E3F8' : 'transparent',
                }}
                onClick={() => {
                  onSelect(i);
                  setOpen(false);
                }}
              >
                <span className="range">{weekRange(w)}</span>
                <span className="ago">{weeksAgoLabel(w.weekStart, today)}</span>
              </button>
            ))}
          </Sheet>
        </Scrim>
      )}
    </>
  );
};

// Only locations with audits that week get a card (see DashboardView).
const LocationCard = ({
  location,
  href,
}: {
  location: DashboardLocation & { score: number };
  href?: string;
}) => {
  const critical = location.score < CRITICAL_SCORE;
  return (
    <Card
      role="listitem"
      style={critical ? { borderColor: '#BA1A1A' } : undefined}
    >
      <div className="head">
        <span
          className="name"
          style={{ color: critical ? '#BA1A1A' : '#325882' }}
        >
          Location {location.name}
        </span>
      </div>
      <div className="body">
        <div
          className="badge"
          style={{ background: scoreColor(location.score) }}
          aria-label={`Score ${location.score}`}
        >
          {location.score}
        </div>
        <div className="audits">
          {location.audits.map((a, i) => (
            <div key={a.auditId}>
              <div className="audit">
                <span className="label" title={a.name || undefined}>
                  {a.name && shortAuditName(a.name)}
                </span>
                <span style={{ color: a.pass ? '#43474E' : '#BA1A1A' }}>
                  {a.passRate === null ? '—' : `${Math.round(a.passRate)}%`}
                </span>
              </div>
              <div className="track">
                <div
                  style={{
                    width: `${a.passRate ?? 0}%`,
                    background: a.pass
                      ? AUDIT_COLORS[i % AUDIT_COLORS.length]
                      : '#BA1A1A',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {href && (
        <Link className="report" to={href}>
          View report →
        </Link>
      )}
    </Card>
  );
};

// Top / lowest store in the banner: its name, linking to its report when there is one.
const StoreLink = ({
  store,
  reportLink,
}: {
  store: DashboardLocationScore;
  reportLink?: (locationId: string) => string;
}) => {
  const href = reportLink?.(store.locationId);
  const text = `Location ${store.name}`;
  return href ? (
    <Link className="store" to={href}>
      {text} →
    </Link>
  ) : (
    <div className="store">{text}</div>
  );
};

const DashboardView = ({
  weeks,
  selected,
  onSelect,
  today,
  reportLink,
}: Props) => {
  const week = weeks[selected];
  const { summary } = week;
  const chartWeeks = [...weeks].reverse();
  return (
    <Page>
      <TitleRow>
        <h1>All locations</h1>
        <WeekSelector
          weeks={weeks}
          selected={selected}
          onSelect={onSelect}
          today={today}
        />
      </TitleRow>
      <section>
        {summary.average !== null && (
          <Hero>
            <div className="main">
              <div className="caption">
                Average Gritsee Score · {weekRange(week)}
              </div>
              <div className="number">
                {summary.average}
                <span>/100</span>
              </div>
              <div className="note">
                Average of the {summary.reporting} locations that reported
              </div>
            </div>
            <div className="kpis">
              {summary.top && (
                <div>
                  <div className="kpi" style={{ color: '#006C50' }}>
                    {summary.top.score}
                  </div>
                  <div className="caption">Top Gritsee Score</div>
                  <StoreLink store={summary.top} reportLink={reportLink} />
                </div>
              )}
              {summary.lowest && (
                <div className="lowest">
                  <div className="kpi" style={{ color: '#BA1A1A' }}>
                    {summary.lowest.score}
                  </div>
                  <div className="caption">Lowest Gritsee Score</div>
                  <StoreLink store={summary.lowest} reportLink={reportLink} />
                </div>
              )}
            </div>
          </Hero>
        )}
      </section>

      <section>
        <SectionTitle>By location</SectionTitle>
        <Cards role="list" aria-label="Locations">
          {/* Locations without audits that week are left out of the dashboard. */}
          {week.locations
            .filter(
              (l): l is DashboardLocation & { score: number } =>
                !l.offline && l.score !== null
            )
            .map((l) => (
              <LocationCard
                key={l.locationId}
                location={l}
                href={reportLink?.(l.locationId)}
              />
            ))}
        </Cards>
      </section>

      <section>
        <SectionTitle>Store score</SectionTitle>
        <StoreScoreChart
          series={scoreSeries(weeks)}
          weekLabels={chartWeeks.map((w) => shortDate(w.weekStart))}
          selected={weeks.length - 1 - selected}
          onSelect={(chartIndex) => onSelect(weeks.length - 1 - chartIndex)}
        />
      </section>
    </Page>
  );
};

const Page = styled.div`
  max-width: 1152px;
  margin: 0 auto;
  padding: 40px 60px 60px;
  color: #191c20;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  font-variant-numeric: tabular-nums;
  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 400;
    line-height: 36px;
  }
  section {
    margin-top: 32px;
  }
  @media (max-width: 640px) {
    padding: 16px 16px 40px;
    h1 {
      font-size: 24px;
      line-height: 32px;
    }
  }
`;

const SectionTitle = styled.div`
  padding-bottom: 12px;
  border-bottom: 1px solid #c3c6cf;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #43474e;
`;

// Title on the left, week selector on the right; on mobile the selector wraps below.
const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

// Desktop: a compact bordered control sized to its content.
// Mobile: full width, arrows at the edges.
const WeekBar = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #c3c6cf;
  border-radius: 8px;
  background: #ffffff;
  button:hover:not(:disabled) {
    background: rgba(25, 28, 32, 0.08);
  }
  @media (max-width: 640px) {
    display: flex;
    width: 100%;
    justify-content: space-between;
    border: none;
    background: none;
  }
`;

const IconButton = styled.button`
  flex: none;
  width: 40px;
  height: 48px;
  border: none;
  border-radius: 8px;
  background: none;
  padding: 0;
  font-size: 28px;
  line-height: 1;
  color: #191c20;
  cursor: pointer;
  &:disabled {
    color: rgba(25, 28, 32, 0.38);
    cursor: default;
  }
`;

const WeekButton = styled.button`
  flex: none;
  min-width: 0;
  min-height: 48px;
  background: none;
  border: none;
  border-left: 1px solid #c3c6cf;
  border-right: 1px solid #c3c6cf;
  padding: 4px 16px;
  text-align: center;
  @media (max-width: 640px) {
    flex: 1;
    border: none;
    border-radius: 8px;
  }
  color: #191c20;
  font: inherit;
  cursor: pointer;
  .range {
    display: block;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
  }
  .ago {
    font-size: 12px;
    line-height: 16px;
    color: #43474e;
  }
`;

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  .backdrop {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
    padding: 0;
    background: rgba(0, 0, 0, 0.32);
  }
`;

const Sheet = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(480px, calc(100% - 32px));
  max-height: 80vh;
  overflow-y: auto;
  border-radius: 28px;
  background: #f2f3fa;
  padding: 0 0 24px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  .title {
    padding: 20px 24px 12px;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
  }
  .item {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 64px;
    padding: 8px 24px;
    border: none;
    text-align: left;
    font: inherit;
    cursor: pointer;
  }
  .range {
    font-size: 16px;
    line-height: 24px;
  }
  .ago {
    color: #43474e;
  }
  @media (max-width: 640px) {
    left: 0;
    right: 0;
    top: auto;
    bottom: 0;
    transform: none;
    width: auto;
    max-height: 85vh;
    border-radius: 28px 28px 0 0;
  }
`;

const Hero = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 32px;
  margin-top: 20px;
  padding: 24px 28px;
  background: #ffffff;
  border: 1px solid #c3c6cf;
  border-radius: 16px;
  .main {
    flex: 1 1 50%;
  }
  .caption {
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: #43474e;
  }
  .number {
    margin-top: 10px;
    font-size: 45px;
    line-height: 52px;
    color: #325882;
    span {
      font-size: 16px;
      color: #43474e;
    }
  }
  .note {
    margin-top: 10px;
    color: #43474e;
  }
  .kpis {
    flex: 1 1 50%;
    display: flex;
    border-left: 1px solid #c3c6cf;
    padding-left: 32px;
    > div {
      flex: 1;
    }
    .lowest {
      border-left: 1px solid #c3c6cf;
      padding-left: 24px;
    }
  }
  .kpi {
    font-size: 28px;
    line-height: 36px;
    margin-bottom: 8px;
  }
  .store {
    display: inline-block;
    margin-top: 4px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #325882;
    text-decoration: none;
  }
  a.store:hover {
    text-decoration: underline;
  }
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    padding: 18px 20px;
    gap: 18px;
    .number {
      font-size: 36px;
      line-height: 44px;
    }
    .kpis {
      border-left: none;
      padding-left: 0;
      border-top: 1px solid #c3c6cf;
      padding-top: 18px;
    }
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(256px, 100%), 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const Card = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid #c3c6cf;
  border-radius: 12px;
  padding: 16px;
  .head {
    min-height: 24px;
  }
  .name {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }
  .body {
    display: flex;
    gap: 12px;
    margin-top: 12px;
    align-items: center;
  }
  .badge {
    flex: none;
    width: 48px;
    height: 48px;
    border-radius: 8px;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
  }
  .audits {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .audit {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
  }
  .label {
    min-width: 0;
    color: #43474e;
    /* One line; the full name is in the title tooltip. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .report {
    display: block;
    margin-top: 12px;
    padding-top: 8px;
    border-top: 1px solid #e1e2e8;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #325882;
    text-decoration: none;
  }
  .report:hover {
    text-decoration: underline;
  }
  /* The whole card opens the report: the link's area covers the card (one link, so keyboard
     and screen readers still see a single "View report"). */
  .report::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
  }
  .report:hover::after {
    box-shadow: 0 2px 8px rgba(25, 28, 32, 0.15);
  }
  .report:focus-visible {
    outline: none;
  }
  .report:focus-visible::after {
    outline: 2px solid #325882;
    outline-offset: 2px;
  }
  .track {
    position: relative;
    height: 4px;
    margin-top: 4px;
    background: #e1e2e8;
    border-radius: 4px;
    overflow: hidden;
    div {
      position: absolute;
      inset: 0 auto 0 0;
      border-radius: 6px;
    }
  }
`;

export default DashboardView;
